import React, { FC, useCallback, useState } from 'react';
import { StudentInterface } from '../../interfaces';
import { useStudentService, useTranslation } from '../../services';
import { Collapse, IconButton, Stack, TableCell, TableRow, Badge, Tooltip } from '@mui/material';
import { Delete, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { Status } from '../Status';
import { EditStudentDialog } from '../EditStudentDialog';
import { ButtonWithNotes } from '../ButtonWithNotes';
import { TextToCopy } from '../TextToCopy';
import style from './ManageStudentsTable.module.css';
import EmailIcon from '@mui/icons-material/Email';
import ErrorIcon from '@mui/icons-material/Error';
import dayjs from 'dayjs';

const ManageStudentsTableRow: FC<{ row: StudentInterface }> = ({ row }) => {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const { deleteStudent, updateStudent } = useStudentService();

	const handleDelete = useCallback(
		async (val: boolean, notes?: string, id?: string) => {
			val && id && (await deleteStudent(id, notes));
		},
		[deleteStudent]
	);

	const handleStudentEdit = useCallback(
		async (data: StudentInterface, id?: string) => {
			if (id) {
				await updateStudent(id, data);
			}
		},
		[updateStudent]
	);
	return (
		<>
			<TableRow key={row.id} sx={{ '& > *': { borderBottom: 'unset !important' } }}>
				<TableCell>
					<IconButton
						aria-label="expand row"
						size="small"
						onClick={() => setOpen(!open)}
					>
						{open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
					</IconButton>
				</TableCell>
				<TableCell component="th" scope="row">
					{row?.requestUnit}
				</TableCell>
				<TableCell component="th" scope="row">
					{row.id && (
						<TextToCopy
							hideText={true}
							direction="column"
							text={row.id}
							title={t('Copy')}
						/>
					)}
				</TableCell>
				<TableCell component="th" scope="row">
					{row.email}
					<div className={style.basicFlowEmail}>{row?.requestBasicFlowEmail}</div>
				</TableCell>
				<TableCell component="th" scope="row">
					{row.name}
				</TableCell>
				<TableCell component="th" scope="row">
				<div className={style.statusCell}>
						<div className={style.statusText}>
							<Status status={row.status} />
						</div>
						{
							row.dublicateCount && (
								<Badge className={style.dublicateCountBadge} badgeContent={row.dublicateCount} color="primary">
									<EmailIcon color="error" />
								</Badge>
							)
						}
						{
							row.error && (
								<Tooltip placement="top" title={row.error}>
									<ErrorIcon className={style.dublicateCountBadge} color="error" />
								</Tooltip>
							)
						}
					</div>
				</TableCell>
				<TableCell component="th" scope="row">
					{dayjs(row.updatedAt).format('DD.MM.YYYY hh:mm')}
				</TableCell>
				<TableCell>{row.description || row.requestDescription}</TableCell>
				<TableCell>
					{row.id && (
						<EditStudentDialog
							update={(i, res) => handleStudentEdit(res, row.id)}
							value={row}
							index={0}
						/>
					)}
					<ButtonWithNotes
						WrappedComponent={Delete}
						callback={(val, notes) => handleDelete(val, notes, row.id)}
						propsWrapped={{
							title: t('Remove student'),
							color: 'error',
						}}
					/>
				</TableCell>
			</TableRow>
			<TableRow>
				<TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
					<Collapse in={open} timeout="auto" unmountOnExit>
						<Stack>
							{row.requestId && (
								<TextToCopy
									text={row.requestId}
									title={t('Copy Request ID')}
									label={t('Request ID')}
								/>
							)}
							{row.requestEmail && (
								<Stack direction="row" alignItems="center" sx={{p:'5px'}}>

										<span>
											{t('Responsible person email')}:&nbsp;
										</span>
										<Stack
											direction="row"
											className={style.infoEmail}
										>
											<div>{row.requestEmail}</div>
										</Stack>

								</Stack>
							)}
							{row.requestBasicFlowEmail && (
								<Stack direction="row" alignItems="center" sx={{p:'5px'}}>

										<span>
											{t('Basic flow email')}:&nbsp;
										</span>
										<Stack
											direction="row"
											className={style.infoEmail}
										>
											<div>{row.requestBasicFlowEmail}</div>
										</Stack>

								</Stack>
							)}
						</Stack>
						<Stack direction="row">
							{/*	TODO: this should be table with info about student progress */}
						</Stack>
					</Collapse>
				</TableCell>
			</TableRow>
		</>
	);
};

export default ManageStudentsTableRow;
